import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Brain, CheckCircle2, ChevronDown, Clock, Gamepad2, Loader2, Play, Plus, Sparkles, Trash2, Calculator } from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PracticeReportButton, WeeklyPlanForm } from "./PracticePlanner";

export const HUBS = {
  abacus: { label: "Abacus Practice", icon: Calculator, cls: "bg-amber-100 text-amber-700" },
  mental: { label: "Mental Maths", icon: Brain, cls: "bg-violet-100 text-violet-700" },
  vedic: { label: "Vedic Maths", icon: Sparkles, cls: "bg-rose-100 text-rose-700" },
};
const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri" };
const STATUS = {
  completed: ["Completed", "bg-emerald-100 text-emerald-700"],
  in_progress: ["In progress", "bg-amber-100 text-amber-700"],
  not_started: ["Not started", "bg-slate-100 text-slate-600"],
};
const errMsg = (e, fallback) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(", ") : (typeof d === "string" ? d : fallback); };
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, Number(v) || lo));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
export const StatusChip = ({ p, testId }) => {
  const [label, cls] = STATUS[p.status];
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`} data-testid={testId}>{label}{p.late ? " · late" : ""}</span>;
};

const AssignForm = ({ classId, headers, onCreated }) => {
  const [cat, setCat] = useState(null);
  const [f, setF] = useState({ hub: "mental", activity: "", difficulty: "beginner", min_accuracy: 60, rounds_required: 1, due_at: "", note: "" });
  const [busy, setBusy] = useState(false);
  const auth = headers.Authorization;
  useEffect(() => { axios.get(`${API}/practice-tasks/catalog`, { headers: { Authorization: auth } }).then((r) => setCat(r.data.catalog)).catch(() => toast.error("Couldn't load the game list")); }, [auth]);
  const games = cat?.[f.hub] || [];
  const activity = f.activity || games[0]?.id || "";
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/classes/${classId}/practice-tasks`, { ...f, activity, min_accuracy: clamp(f.min_accuracy, 0, 100), rounds_required: clamp(f.rounds_required, 1, 10), due_at: f.due_at ? new Date(f.due_at).toISOString() : null }, { headers });
      toast.success("Practice assigned - students have been notified");
      setF({ ...f, note: "", due_at: "" });
      onCreated();
    } catch (e) { toast.error(errMsg(e, "Couldn't assign")); } finally { setBusy(false); }
  };
  const sel = "mt-1 w-full h-10 rounded-md border border-input bg-white px-2 text-sm";
  return (
    <div className="rounded-2xl border bg-white p-4 space-y-3" data-testid="practice-assign-form">
      <p className="font-semibold flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-primary" />Assign a practice game</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(HUBS).map(([k, h]) => (
          <button key={k} onClick={() => setF({ ...f, hub: k, activity: "" })} className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${f.hub === k ? "bg-primary text-white border-primary" : "bg-white hover:bg-slate-50"}`} data-testid={`practice-hub-${k}`}>{h.label}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <label className="text-xs font-semibold text-slate-600">Game
          <select className={sel} value={activity} onChange={(e) => setF({ ...f, activity: e.target.value })} data-testid="practice-activity">
            {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Level (or harder)
          <select className={sel} value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })} data-testid="practice-level">
            {["beginner", "easy", "medium", "hard", "expert"].map((l) => <option key={l} value={l}>{l[0].toUpperCase() + l.slice(1)}</option>)}
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Due (optional)
          <Input type="datetime-local" className="mt-1" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} data-testid="practice-due" /></label>
        <label className="text-xs font-semibold text-slate-600">Pass mark (accuracy %)
          <Input type="number" min={0} max={100} className="mt-1" value={f.min_accuracy} onChange={(e) => setF({ ...f, min_accuracy: e.target.value })} data-testid="practice-min-accuracy" /></label>
        <label className="text-xs font-semibold text-slate-600">Rounds needed
          <Input type="number" min={1} max={10} className="mt-1" value={f.rounds_required} onChange={(e) => setF({ ...f, rounds_required: e.target.value })} data-testid="practice-rounds" /></label>
        <label className="text-xs font-semibold text-slate-600">Note for students (optional)
          <Input className="mt-1" maxLength={300} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="practice-note" /></label>
      </div>
      {games.find((g) => g.id === activity)?.desc && <p className="text-xs text-muted-foreground">{games.find((g) => g.id === activity).desc}</p>}
      <Button onClick={submit} disabled={busy || !activity} data-testid="practice-assign-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Assign to class</Button>
    </div>
  );
};

const TaskMeta = ({ t }) => (
  <p className="text-xs text-muted-foreground">
    {t.plan_day && <span className="font-semibold text-primary" data-testid={`practice-day-${t.task_id}`}>{DAY_LABELS[t.plan_day]} · </span>}{HUBS[t.hub].label} · {t.difficulty[0].toUpperCase() + t.difficulty.slice(1)}+ · {t.rounds_required} round{t.rounds_required > 1 ? "s" : ""} at {t.min_accuracy}%+
    {t.due_at && <> · <Clock className="inline w-3 h-3 -mt-0.5" /> due {fmtDate(t.due_at)}</>}
  </p>
);

const TeacherTask = ({ t, headers, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const H = HUBS[t.hub];
  const toggle = async () => {
    setOpen(!open);
    if (open) return;
    try { setRows((await axios.get(`${API}/practice-tasks/${t.task_id}/progress`, { headers })).data.students); }
    catch (e) { toast.error(errMsg(e, "Couldn't load progress")); setOpen(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove "${t.activity_name}" from this class? Students' scores are kept.`)) return;
    try { await axios.delete(`${API}/practice-tasks/${t.task_id}`, { headers }); toast.success("Practice removed"); onDeleted(); }
    catch (e) { toast.error(errMsg(e, "Couldn't remove")); }
  };
  const pct = t.summary.total ? Math.round((t.summary.completed / t.summary.total) * 100) : 0;
  return (
    <div className="rounded-2xl border bg-white" data-testid={`practice-task-${t.task_id}`}>
      <div className="p-4 flex flex-wrap items-center gap-3">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${H.cls}`}><H.icon className="w-5 h-5" /></span>
        <div className="flex-1 min-w-[180px]">
          <p className="font-semibold">{t.activity_name}</p>
          <TaskMeta t={t} />
          {t.note && <p className="text-xs mt-0.5 text-slate-600 italic">"{t.note}"</p>}
        </div>
        <div className="w-40">
          <p className="text-xs font-semibold text-right" data-testid={`practice-summary-${t.task_id}`}>{t.summary.completed}/{t.summary.total} completed</p>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-1"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        </div>
        <Button size="sm" variant="outline" onClick={toggle} data-testid={`practice-view-${t.task_id}`}>Who completed it <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${open ? "rotate-180" : ""}`} /></Button>
        <Button size="icon" variant="ghost" className="text-red-600" onClick={del} data-testid={`practice-delete-${t.task_id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
      {open && (
        <div className="border-t overflow-x-auto" data-testid={`practice-progress-${t.task_id}`}>
          {!rows ? <div className="p-4"><Loader2 className="w-4 h-4 animate-spin" /></div> : !rows.length ? <p className="p-4 text-sm text-muted-foreground">No students in this class yet.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="px-4 py-2">Student</th><th>Status</th><th>Rounds passed</th><th>Played</th><th>Best accuracy</th><th>Completed on</th><th>Last played</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.student_id} className="border-t" data-testid={`practice-row-${r.student_id}`}>
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td><StatusChip p={r} testId={`practice-status-${r.student_id}`} /></td>
                    <td>{r.rounds_qualifying}/{t.rounds_required}</td>
                    <td>{r.rounds_played}</td>
                    <td>{r.best_accuracy ?? "—"}{r.best_accuracy != null && "%"}</td>
                    <td className="text-xs text-muted-foreground">{fmtDate(r.completed_at) || "—"}</td>
                    <td className="text-xs text-muted-foreground">{fmtDate(r.last_played) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export const StudentTask = ({ t, showClass = false }) => {
  const navigate = useNavigate();
  const H = HUBS[t.hub];
  const p = t.my_progress;
  return (
    <div className="rounded-2xl border bg-white p-4 flex flex-wrap items-center gap-3" data-testid={`my-practice-${t.task_id}`}>
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${H.cls}`}>{p.completed ? <CheckCircle2 className="w-5 h-5" /> : <H.icon className="w-5 h-5" />}</span>
      <div className="flex-1 min-w-[160px]">
        <p className="font-semibold">{t.activity_name} {showClass && <span className="text-xs font-normal text-muted-foreground">· {t.class_name}</span>}</p>
        <TaskMeta t={t} />
        {t.note && <p className="text-xs mt-0.5 text-slate-600 italic">"{t.note}"</p>}
        {t.hub === "abacus" && <p className="text-[11px] text-muted-foreground">Use Practice or Timed mode - Learn mode doesn't count.</p>}
        <p className="text-xs mt-1">Rounds passed: <b>{p.rounds_qualifying}/{t.rounds_required}</b>{p.best_accuracy != null && <> · best {p.best_accuracy}%</>}</p>
      </div>
      <StatusChip p={p} testId={`my-practice-status-${t.task_id}`} />
      <Button size="sm" onClick={() => navigate(t.play_link)} data-testid={`my-practice-play-${t.task_id}`}><Play className="w-4 h-4 mr-1" />{p.completed ? "Play again" : "Play now"}</Button>
    </div>
  );
};

const TeacherTools = ({ classId, headers, onCreated }) => {
  const [mode, setMode] = useState("single");
  const btn = (k, label) => <button onClick={() => setMode(k)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${mode === k ? "bg-white shadow-sm text-primary" : "text-slate-600 hover:text-slate-900"}`} data-testid={`practice-mode-${k}`}>{label}</button>;
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="inline-flex rounded-xl bg-slate-100 p-1">{btn("single", "Single game")}{btn("week", "Weekly plan")}</div>
        <PracticeReportButton classId={classId} headers={headers} />
      </div>
      {mode === "single" ? <AssignForm classId={classId} headers={headers} onCreated={onCreated} /> : <WeeklyPlanForm classId={classId} headers={headers} onCreated={onCreated} />}
    </>
  );
};

export const PracticeTab = ({ classId, isTeacher, headers }) => {
  const [tasks, setTasks] = useState(null);
  const auth = headers.Authorization;
  const load = useCallback(() => axios.get(`${API}/classes/${classId}/practice-tasks`, { headers: { Authorization: auth } }).then((r) => setTasks(r.data.tasks)).catch(() => setTasks([])), [classId, auth]);
  useEffect(() => { load(); }, [load]);
  const pending = useMemo(() => (tasks || []).filter((t) => !t.my_progress?.completed).length, [tasks]);
  return (
    <div className="space-y-4" data-testid="practice-tab">
      {isTeacher && <TeacherTools classId={classId} headers={headers} onCreated={load} />}
      {!isTeacher && tasks?.length > 0 && <p className="text-sm text-muted-foreground">{pending ? `${pending} practice game${pending > 1 ? "s" : ""} to complete` : "All practice completed - great work!"}</p>}
      {tasks === null ? <Loader2 className="w-5 h-5 animate-spin" /> : !tasks.length ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="practice-empty">{isTeacher ? "No practice games assigned yet." : "Your teacher hasn't assigned any practice games yet."}</p>
      ) : tasks.map((t) => (isTeacher ? <TeacherTask key={t.task_id} t={t} headers={headers} onDeleted={load} /> : <StudentTask key={t.task_id} t={t} />))}
    </div>
  );
};

// Student dashboard: pending teacher-assigned practice across all classes
export const MyPracticeCard = ({ token }) => {
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    if (token) axios.get(`${API}/student/practice-tasks`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setTasks(r.data.tasks)).catch(() => {});
  }, [token]);
  const pending = tasks.filter((t) => !t.my_progress.completed);
  if (!tasks.length) return null;
  return (
    <section className="mb-6" data-testid="my-practice-card">
      <h2 className="text-xl font-bold text-slate-800 font-['Fredoka'] mb-3">Practice from your teacher {pending.length > 0 && <span className="ml-1 text-sm align-middle px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{pending.length} to do</span>}</h2>
      <div className="space-y-2">
        {(pending.length ? pending : tasks.slice(0, 2)).slice(0, 4).map((t) => <StudentTask key={t.task_id} t={t} showClass />)}
      </div>
    </section>
  );
};
